// The routes.
//
// Eight surfaces, seven routes: pending actions have no route because they render inside
// the group conversation. Private chat and Conversation share a data pipeline (`usePlan`,
// one per plan) but are separate routes, so a reader can be in one and stay there across a
// signal or a poll instead of being switched under them. Costs and What’s new are not
// plan-scoped, so they sit at the top level rather than nested under `/plans/:planId`.
//
// Two more since: `/demo` (the scenario list) sits at the top level, the same as Costs
// and What's new — reached from the plans list, not from inside a plan.
// `/plans/:planId/sheet` is plan-scoped precisely because a character sheet belongs to
// one plan's demo, not to the scenario list.
//
// One more since that: `/demo/:scenarioId/:seatLabel` (`CharacterPreview.tsx`) sits
// alongside `/demo` rather than under `/plans`, because looking at a character's brief
// creates no plan — the scenario and seat in its own address are all it needs, and
// nothing about a plan exists yet for it to be scoped under.
//
// The client is constructed once, above the router, and reads the token at call time
// rather than capturing it — so signing in does not have to rebuild anything, and a token
// that goes away mid-session produces a 401 from the server rather than a stale header.

import { lazy, Suspense, useEffect, useMemo, useSyncExternalStore } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { ApiClient, type Fetch } from './api/client.js';
import { Header } from './components/Header.js';
import { pendingInviteToken, subscribeInviteStash } from './invite-stash.js';
import { pendingReturnPath, stashReturnPath } from './return-path-stash.js';
import { session } from './session.js';
import type { Internals } from './internals.js';
import { CharacterPreview } from './surfaces/CharacterPreview.js';
import { CharacterSheet } from './surfaces/CharacterSheet.js';
import { DemoScenarios } from './surfaces/DemoScenarios.js';
import { Invite } from './surfaces/Invite.js';
import { PlanScreen } from './surfaces/PlanScreen.js';
import { Plans } from './surfaces/Plans.js';
import { SignIn, SignUp } from './surfaces/SignIn.js';
import { WhatsNew } from './surfaces/WhatsNew.js';

// Vite removes this import and its chunk from builds where the operator flag is absent,
// the same gate `PlanScreen.tsx` uses for its own operator panel.
const CostsReport =
  import.meta.env.VITE_MOR_OPERATOR_ENABLED === 'true'
    ? lazy(() =>
        import('./operator/CostsReport.js').then((module) => ({
          default: module.CostsReport,
        })),
      )
    : null;

export type AppProps = {
  baseUrl: string;
  fetchImpl?: Fetch;
  internals?: Internals;
  live?: boolean;
  pollMs?: number;
};

export function App({
  baseUrl,
  fetchImpl,
  internals,
  live,
  pollMs,
}: AppProps): JSX.Element {
  const token = useSyncExternalStore(
    session.subscribe,
    () => session.token(),
    () => null,
  );
  const waitingInvite = useSyncExternalStore(
    subscribeInviteStash,
    pendingInviteToken,
    () => null,
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Stashed while still signed out, so a deep link followed cold (the costs report link
  // from the debug panel, opened in a fresh tab with no session yet) survives the sign-in
  // vendor's own redirect instead of silently becoming the plans list. The signed-out
  // screens themselves need no stash: `/signup` and `/signin` are the destination, and
  // `/invite` already stashes its own token.
  useEffect(() => {
    const gettingIn = ['/signup', '/signin', '/invite'].includes(location.pathname);
    if (token === null && !gettingIn) {
      stashReturnPath(location.pathname + location.search);
    }
  }, [token, location]);

  // Consumed here, as a plain effect, rather than as a route the signed-in branch renders
  // (a `pendingReturn !== null` conditional next to the others below): that shape made the
  // stash-clear and the `<Navigate>` two separate effects on two different timelines, and
  // the render caught in the gap between them saw the stash already empty but the location
  // still `/signup` — whose OWN route rule redirects to `/`, which is exactly the
  // plans-list landing this exists to prevent. A plain top-level effect has no such gap:
  // React always runs a route element's own effects (like `/signup`'s redirect) before
  // this one, being the parent, so this always fires last and wins.
  useEffect(() => {
    if (token === null) {
      return;
    }
    const target = pendingReturnPath();
    if (target !== null) {
      stashReturnPath(null);
      navigate(target, { replace: true });
    }
  }, [token, navigate]);

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl,
        token: () => session.token(),
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      }),
    [baseUrl, fetchImpl],
  );

  const plan = (
    tab: 'private' | 'principals' | 'conversation' | 'decisions' | 'people',
  ): JSX.Element => (
    <PlanScreen
      client={client}
      tab={tab}
      {...(internals === undefined ? {} : { internals })}
      {...(live === undefined ? {} : { live })}
      {...(pollMs === undefined ? {} : { pollMs })}
    />
  );

  return (
    <>
      {/* Rendered once here, rather than duplicated into `Plans.tsx` and `PlanScreen.tsx`
          separately. The bar itself renders on every route, including `/signup` and
          `/invite` while signed out; only the account trigger inside it gates on the
          token, so this component needs no knowledge of which route it is on. */}
      <Header client={client} />
      <Routes>
        {/* The invitation route is reachable signed out, because that is the state the person
            following the link from their mail is in. It says so rather than redirecting,
            which would drop the fragment. */}
        <Route path="/invite" element={<Invite client={client} />} />
        {token === null ? (
          <>
            {/* Signed out, the default is signing UP: an invitee has `/invite`, and
                everybody else arriving here has no account yet. `/signin` stays one link
                away, on the vendor's own card and on this redirect's sibling route. */}
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="*" element={<Navigate to="/signup" replace />} />
          </>
        ) : waitingInvite !== null ? (
          // A signed-in person with an invitation still waiting in the tab's stash. The
          // sign-in vendor decides for itself where to land after authenticating, and the
          // first live redemption showed it landing here, on the plans list, with the
          // token sitting unused — so every route walks the person back to the invite
          // screen until the stash is spent, which redeeming (or failing) does.
          <Route path="*" element={<Navigate to="/invite" replace />} />
        ) : (
          <>
            <Route path="/" element={<Plans client={client} />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/signin" element={<Navigate to="/" replace />} />
            <Route path="/whats-new" element={<WhatsNew client={client} />} />
            <Route path="/demo" element={<DemoScenarios client={client} />} />
            <Route
              path="/demo/:scenarioId/:seatLabel"
              element={<CharacterPreview client={client} />}
            />
            <Route
              path="/plans/:planId/sheet"
              element={<CharacterSheet client={client} />}
            />
            <Route
              path="/costs"
              element={
                CostsReport === null ? (
                  <Navigate to="/" replace />
                ) : (
                  <Suspense fallback={null}>
                    <CostsReport client={client} />
                  </Suspense>
                )
              }
            />
            <Route path="/plans/:planId" element={plan('conversation')} />
            <Route path="/plans/:planId/private" element={plan('private')} />
            <Route path="/plans/:planId/principals" element={plan('principals')} />
            <Route path="/plans/:planId/decisions" element={plan('decisions')} />
            <Route path="/plans/:planId/people" element={plan('people')} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </>
  );
}
